import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { ScaleFade } from '@fiverr-private/transition';
import { I18n } from '@fiverr-private/i18n-react';
import { BiEventData, onImpression } from '@fiverr-private/store_builder_bi_events';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { FloatingModalPopup } from '../floating-modal-popup/FloatingModalPopup';
import { FreelanceNetworkIcon } from './FreelanceNetworkIcon';
import { trigger, fixedContainer } from './FloatingModal.ve.css';

const FixedContainer = ({ children }: { children: ReactNode }) => (
    <Container position="fixed" bottom="23px" right="92px" className={fixedContainer}>
        {children}
    </Container>
);

interface FloatingModalProps {
    manualOpening: boolean;
    pageName: string;
    biEventData: BiEventData;
}

export const FloatingModal = ({ manualOpening = false, pageName, biEventData }: FloatingModalProps) => {
    const [showTrigger, setShowTrigger] = useState(true);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: 'viewed_bys_sticky_component',
            componentName: 'bys_sticky_component',
            pageName,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const hasOpenedBefore = useRef(false);

    const handleOpen = () => {
        setIsPopupOpen(true);
        hasOpenedBefore.current = true;
    };

    const handleManualOpening = () => {
        handleOpen();
    };

    useEffect(() => {
        if (manualOpening && !hasOpenedBefore.current) {
            handleOpen();
        }
    }, [manualOpening]);

    return (
        <Container ref={pageImpressionRef}>
            {/* Popup */}
            <FixedContainer>
                <ScaleFade
                    in={isPopupOpen}
                    easing={{ enter: 'easeIn', exit: 'easeOut' }}
                    duration={{ enter: 'slow1', exit: 'moderate1' }}
                    delay="moderate1"
                    onAnimationStart={() => isPopupOpen && setShowTrigger(false)}
                    onAnimationEnd={() => {
                        if (!isPopupOpen) {
                            setShowTrigger(true);
                        }
                    }}
                >
                    <Container position="fixed" bottom="16px" right="92px" zIndex="overlay">
                        <FloatingModalPopup
                            setIsPopupOpen={setIsPopupOpen}
                            biEventData={biEventData}
                            pageName={pageName}
                        />
                    </Container>
                </ScaleFade>
            </FixedContainer>

            {/* Trigger */}
            <FixedContainer>
                <ScaleFade in={showTrigger} easing={{ enter: 'easeIn' }} duration={{ enter: 'slow1' }}>
                    <Container
                        onClick={handleManualOpening}
                        display="flex"
                        paddingY="2"
                        paddingX="4"
                        gap="2"
                        cursor="pointer"
                        justifyContent="center"
                        borderRadius="2xl"
                        alignItems="center"
                        boxShadow="z3"
                        maxHeight="56px"
                        background="white"
                        borderWidth="sm"
                        className={trigger}
                    >
                        <FreelanceNetworkIcon />
                        <Text fontWeight="semibold" size="b_sm">
                            <I18n k="store_builder_ui.floating_modal.title" />
                        </Text>
                    </Container>
                </ScaleFade>
            </FixedContainer>
        </Container>
    );
};
